import { useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Loader2, Eye, EyeOff } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, errMsg, useFetch, fmtDate } from "@/lib/api";
import { divisionName } from "@/lib/content";
import { FieldInput } from "@/components/admin/FieldInput";
import { SCHEMAS } from "@/components/admin/schemas";

const cell = (k, v) => (k === "date" ? fmtDate(v) : ["division", "team_slug"].includes(k) ? (v === "all" ? "All" : divisionName(v)) : String(v ?? ""));

const Editor = ({ kind, item, user, onClose, onSaved }) => {
  const schema = SCHEMAS[kind];
  const fields = schema.fields.filter((f) => !(f.adminOnly && user.role !== "admin") && !(f.coachLocked && user.role === "coach"));
  const init = item || Object.fromEntries(schema.fields.filter((f) => f.default !== undefined).map((f) => [f.k, f.default]));
  const [form, setForm] = useState(init);
  const [busy, setBusy] = useState(false);
  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (item?.id) await api.put(`/manage/${kind}/${item.id}`, form);
      else await api.post(`/manage/${kind}`, form);
      toast.success("Saved");
      onSaved();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-ink border-line" data-testid={`${kind}-editor-dialog`}>
        <DialogHeader><DialogTitle className="font-display text-3xl font-black uppercase italic">{item?.id ? "Edit" : "Add"} {schema.label}</DialogTitle></DialogHeader>
        <form onSubmit={save} className="space-y-5">
          {fields.map((f) => (
            <div key={f.k}>
              <label className="label">{f.label}{f.required ? " *" : ""}</label>
              <FieldInput field={f} value={form[f.k]} onChange={(v) => setForm((p) => ({ ...p, [f.k]: v }))} prefix={`${kind}-field`} />
            </div>
          ))}
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="btn-line !py-2 !text-sm" data-testid={`${kind}-editor-cancel`}>Cancel</button>
            <button type="submit" disabled={busy} className="btn-gold !py-2.5 !text-sm" data-testid={`${kind}-editor-save`}>{busy && <Loader2 className="h-4 w-4 animate-spin" />}Save</button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export const ContentManager = ({ kind, user }) => {
  const schema = SCHEMAS[kind];
  const { data, loading, reload } = useFetch(`/manage/${kind}`);
  const [editing, setEditing] = useState(null);
  const canAdd = !(kind === "teams" && user.role !== "admin");
  const remove = async (item) => {
    if (!window.confirm(`Delete "${item[schema.cols[0]]}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/manage/${kind}/${item.id}`);
      toast.success("Deleted");
      reload();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };
  return (
    <div data-testid={`${kind}-manager`}>
      <div className="flex items-center justify-between mb-5">
        <h2 className="font-display text-3xl font-black uppercase italic">{schema.label}</h2>
        {canAdd && <button onClick={() => setEditing({})} className="btn-gold !py-2.5 !text-sm" data-testid={`${kind}-add-button`}><Plus className="h-4 w-4" /> Add New</button>}
      </div>
      {kind === "athletes" && <p className="mb-4 text-xs text-slate-400">Athletes appear publicly only when both "Public" and "Parent/guardian permission on file" are on.</p>}
      <div className="card-navy overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="border-b border-line text-left font-mono text-[10px] uppercase tracking-[0.2em] text-gold">{schema.cols.map((c) => <th key={c} className="p-3">{c.replace("_", " ")}</th>)}<th className="p-3">Visibility</th><th className="p-3 text-right">Actions</th></tr></thead>
          <tbody>
            {loading && <tr><td className="p-4 text-slate-400" colSpan={9}>Loading…</td></tr>}
            {!loading && (data || []).length === 0 && <tr><td className="p-4 text-slate-400" colSpan={9} data-testid={`${kind}-empty`}>Nothing here yet.</td></tr>}
            {(data || []).map((item) => {
              const pub = kind === "athletes" ? item.published && item.guardian_consent : kind === "teams" ? item.roster_public : kind === "portal_updates" ? false : item.published;
              return (
                <tr key={item.id} className="border-b border-line/50 last:border-0 hover:bg-elev/50" data-testid={`${kind}-row-${item.id}`}>
                  {schema.cols.map((c) => <td key={c} className="p-3 max-w-[260px] truncate">{cell(c, item[c])}</td>)}
                  <td className="p-3">{pub ? <span className="inline-flex items-center gap-1 text-emerald-400"><Eye className="h-3.5 w-3.5" />Public</span> : <span className="inline-flex items-center gap-1 text-slate-400"><EyeOff className="h-3.5 w-3.5" />Private</span>}</td>
                  <td className="p-3 text-right whitespace-nowrap">
                    <button onClick={() => setEditing(item)} className="p-2 text-slate-300 hover:text-gold" aria-label="Edit" data-testid={`${kind}-edit-${item.id}`}><Pencil className="h-4 w-4" /></button>
                    {canAdd && <button onClick={() => remove(item)} className="p-2 text-slate-300 hover:text-red-400" aria-label="Delete" data-testid={`${kind}-delete-${item.id}`}><Trash2 className="h-4 w-4" /></button>}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {editing && <Editor kind={kind} item={editing.id ? editing : null} user={user} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); reload(); }} />}
    </div>
  );
};
